import { isEqual } from 'lodash'
import { makeAutoObservable, runInAction } from 'mobx'

import { injectable, decorate, inject } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataAny, APIDataBlueUser } from '@evertel/types'
import { DocumentHistoryStore, APIDataDocumentHistoryEntry, DocumentStore } from '../store'

interface Version {
    data: APIDataAny
    name: string
    ownerId?: number
    ownerName?: string
    createdDate: Date
    createdBy?: APIDataBlueUser
    isCurrent: boolean
}

interface DocumentVersionDiff {
    changedFieldIds: Set<string>
    nameChanged: boolean
    ownerChanged: boolean
}

// Prevents a phantom duplicate version from the mandatory PUT that follows document creation when media is attached.
const CREATION_MERGE_WINDOW_MS = 30_000

class DocumentVersionHistoryController {
    documentId = 0
    currentOwnerId: number | null = null
    currentOwnerName: string | null = null

    constructor(
        private api: Api,
        private documentStore: DocumentStore,
        private documentHistoryStore: DocumentHistoryStore
    ) {
        makeAutoObservable(this)
    }

    init = async (documentId: number) => {
        this.documentId = documentId

        const history = await this.api.Routes.Document.getHistory(documentId, {
            include: ['createdBy'],
            order: 'createdDate ASC'
        })

        runInAction(() => {
            this.documentHistoryStore.update(history as APIDataDocumentHistoryEntry[])
        })
    }

    get document() {
        return this.documentStore.findById(this.documentId)
    }

    get historyEntries() {
        return this.documentHistoryStore.find(h => h.objectId === this.documentId)
    }

    get createdEntry() {
        return this.historyEntries.find(h => h.action === 'created')
    }

    get updateEntries() {
        return this.historyEntries
            .filter(h => h.action === 'updated' && (h.data?.changeSet?.data || h.data?.changeSet?.name))
            .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
    }

    get ownershipEntries() {
        return this.historyEntries
            .filter(h => h.action === 'document_ownership_updated')
            .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
    }

    setCurrentOwner = (id?: number, name?: string) => {
        this.currentOwnerId = id ?? null
        this.currentOwnerName = name ?? null
    }

    get versions(): Version[] {
        const doc = this.document
        if (!doc) return []

        const created = this.createdEntry

        // the update immediately following creation is the mandatory data-fill PUT,
        // not a real edit, when it's by the same user and lands inside the window
        const rawUpdates = this.updateEntries
        const firstUpdateIsCreationFill = created && rawUpdates.length > 0 &&
            rawUpdates[0].blueUserId === created.blueUserId &&
            new Date(rawUpdates[0].createdDate).getTime() - new Date(created.createdDate).getTime() <= CREATION_MERGE_WINDOW_MS

        const updates = firstUpdateIsCreationFill ? rawUpdates.slice(1) : rawUpdates

        let versions: Version[]

        if (updates.length === 0) {
            versions = [{
                data: doc.data,
                name: doc.name,
                createdDate: created?.createdDate ?? doc.createdDate,
                createdBy: created?.createdBy,
                isCurrent: true
            }]
        } else {
            versions = new Array(updates.length + 1)

            versions[updates.length] = {
                data: doc.data,
                name: doc.name,
                createdDate: updates[updates.length - 1].createdDate,
                createdBy: updates[updates.length - 1].createdBy,
                isCurrent: true
            }

            // changeSet.data is a per-field patch map ({fieldId: {from, to}}), not a
            // whole-blob {from, to} pair — so each earlier version is reconstructed by
            // copying the next (newer) version and reverting only the fields that entry
            // actually touched, leaving every untouched field as it was.
            for (let i = updates.length - 1; i >= 0; i--) {
                const next = versions[i + 1]
                const entry = updates[i]
                const dataPatch = entry.data?.changeSet?.data ?? {}
                const namePatch = entry.data?.changeSet?.name

                const data = { ...next.data }
                for (const fieldId of Object.keys(dataPatch)) {
                    data[fieldId] = dataPatch[fieldId].from
                }

                versions[i] = {
                    data,
                    name: namePatch ? namePatch.from : next.name,
                    createdDate: i === 0 ? (created?.createdDate ?? doc.createdDate) : updates[i - 1].createdDate,
                    createdBy: i === 0 ? created?.createdBy : updates[i - 1].createdBy,
                    isCurrent: false
                }
            }
        }

        const ownerEvents = this.ownershipEntries
        const lastIndex = versions.length - 1

        return versions.map((version, index) => {
            // the current version can't be resolved from history timestamps — an
            // ownership change can happen independently of any content edit, so
            // there's no reliable "now" to compare history events against. Use the
            // real, live owner (ACL-based) instead.
            if (index === lastIndex) {
                return {
                    ...version,
                    ownerId: this.currentOwnerId ?? undefined,
                    ownerName: this.currentOwnerName ?? undefined
                }
            }

            // the latest transfer at or before this version's date tells us who
            // owned it as of that point; none yet means it predates any transfer,
            // so the document's original creator (its initial OWNER ACL) is correct
            const latestEvent = ownerEvents
                .filter(event => new Date(event.createdDate) <= new Date(version.createdDate))
                .at(-1)

            return {
                ...version,
                ownerId: latestEvent?.data?.newOwnerId ?? created?.createdBy?.id,
                ownerName: latestEvent?.data?.newOwnerName
            }
        })
    }

    diffAgainstPrevious = (index: number): DocumentVersionDiff => {
        const versions = this.versions
        const version = versions[index]
        const previous = versions[index - 1]

        if (!version || !previous) {
            return { changedFieldIds: new Set(), nameChanged: false, ownerChanged: false }
        }

        const changedFieldIds = new Set<string>()
        const allFieldIds = new Set([
            ...Object.keys(previous.data ?? {}),
            ...Object.keys(version.data ?? {})
        ])

        for (const fieldId of allFieldIds) {
            if (!isEqual(previous.data?.[fieldId], version.data?.[fieldId])) {
                changedFieldIds.add(fieldId)
            }
        }

        return {
            changedFieldIds,
            nameChanged: version.name !== previous.name,
            ownerChanged: version.ownerId !== previous.ownerId
        }
    }
}

decorate(injectable(), DocumentVersionHistoryController)
decorate(inject(Api), DocumentVersionHistoryController, 0)
decorate(inject(DocumentStore), DocumentVersionHistoryController, 1)
decorate(inject(DocumentHistoryStore), DocumentVersionHistoryController, 2)

export { DocumentVersionHistoryController, Version, DocumentVersionDiff }
